/**
 * The PRISM Graphics editor, embedded (0.92.0): a full-screen layer over PRISM with the editor's iframe, opened from
 * the Graphics area, a graphics asset's page or a graphics clip in the sequence editor. The page underneath keeps its
 * state, so closing the editor returns to exactly where it was opened.
 *
 * The host side of the bridge (the vendored @prism-graphics/bridge/host, through ./editorHost.ts) mints an editor
 * token for the editor (never in a URL) and refreshes it before it runs out. A save in the editor refreshes the Graphics area and the asset's page. While the editor has
 * unsaved changes, leaving (reload, closing the tab, Back, a link) asks first.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, X } from 'lucide-react';
import { api, errorMessage } from '../api/client';
import { useGraphicsSettings, useMe } from '../api/hooks';
import { queryKeys } from '../api/queryKeys';
import { toast } from '../components/Toasts';
import { CLOSE_TIMEOUT_MS, closeQuestion, createHost, editorInit, saveProblem, type BridgeLogger, type EditorFormat, type Host, type Token } from './editorHost';
import { editorDefaultsFor } from './editorDefaults';

export interface EditorRequest {
  mode: 'new' | 'edit';
  /** The graphic to edit, or (with duplicate) to start a copy from. */
  assetId?: string;
  duplicate?: boolean;
  /** The collection a new graphic is saved into. */
  folderId?: string;
  /** From the sequence the editor was opened from (editorDefaultsFor); otherwise 1920×1080 at 25 fps. */
  defaults?: EditorFormat;
  /** What the top bar says, e.g. the graphic's name. */
  title?: string;
}

export { DEFAULT_EDITOR_FORMAT } from './editorDefaults';

let current: EditorRequest | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Opens and closes the editor layer from anywhere in the app. */
export const graphicsEditor = {
  open(request: EditorRequest) {
    current = { ...request };
    emit();
  },
  close() {
    current = null;
    emit();
  },
  get request() { return current; },
};

/**
 * Closes the editor from its own controls (Close, or the editor's editor:close): the history entry pushed when it
 * opened goes too, so Back then leaves the page underneath as usual.
 */
function leaveEditor() {
  if (!current) return;
  if (window.history.state?.prismGraphicsEditor) window.history.back();
  graphicsEditor.close();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

const LEAVE_MESSAGE = 'PRISM Graphics has unsaved changes. Leave without saving?';

/** What the bridge logs goes to the console, as message types and reasons only. */
const consoleLogger: BridgeLogger = {
  debug: (m, d) => console.debug(m, d ?? ''),
  info: (m, d) => console.info(m, d ?? ''),
  warn: (m, d) => console.warn(m, d ?? ''),
  error: (m, d) => console.error(m, d ?? ''),
};

async function mintToken(): Promise<Token> {
  const r = await api<{ token: string; expiresAt: string }>('/api/v1/graphics/editor-token', { method: 'POST', json: {} });
  return { value: r.token, expiresAt: r.expiresAt };
}

/** Mounted once in the shell; shows the editor while one is open. */
export function GraphicsEditorHost() {
  const request = useSyncExternalStore(subscribe, () => current);
  if (!request) return null;
  return <GraphicsEditorLayer key={JSON.stringify(request)} request={request} />;
}

function GraphicsEditorLayer({ request }: { request: EditorRequest }) {
  const settings = useGraphicsSettings();
  const me = useMe().data;
  const qc = useQueryClient();
  const frame = useRef<HTMLIFrameElement>(null);
  const hostRef = useRef<Host | null>(null);
  const [status, setStatus] = useState<'connecting' | 'ready' | 'error'>('connecting');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<'save' | 'close' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const origin = settings.data?.editorOrigin ?? null;

  // The bridge: created once the iframe (and its src) exists, gone with the layer.
  useEffect(() => {
    if (!origin || !frame.current || !me) return;
    const host = createHost({
      iframe: frame.current,
      editorOrigin: origin,
      init: async () => editorInit({ ...request, defaults: editorDefaultsFor(request.defaults) }, me.user, await mintToken()),
      refreshToken: mintToken,
      on: {
        ready: () => { setStatus('ready'); setProblem(null); },
        dirty: ({ dirty: d }) => setDirty(d),
        saved: ({ assetId, version, name }) => {
          setDirty(false);
          void qc.invalidateQueries({ queryKey: queryKeys.graphicsAll() });
          void qc.invalidateQueries({ queryKey: queryKeys.assets() });
          void qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
          void qc.invalidateQueries({ queryKey: queryKeys.templates() });
          toast(`Saved “${name}” as version ${version}`);
        },
        close: () => leaveEditor(),
        error: ({ code, message }) => {
          if (code === 'TOKEN_REFRESH_FAILED') setProblem('The connection to PRISM Graphics expired. Save your work elsewhere, close the editor and open it again.');
          else if (code === 'INIT_INVALID') { setStatus('error'); setProblem(`PRISM Graphics could not be started: ${message}`); }
          else toast(`PRISM Graphics: ${message}`, 'error');
        },
      },
      logger: consoleLogger,
    });
    hostRef.current = host;
    return () => {
      host.destroy();
      hostRef.current = null;
    };
  }, [origin, me?.user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving with unsaved changes asks first: reload or closing the tab, links in PRISM, and Back.
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      if (!dirtyRef.current) return;
      const a = (e.target as Element | null)?.closest?.('a[href]');
      if (a && !window.confirm(LEAVE_MESSAGE)) {
        e.preventDefault();
        e.stopPropagation();
      } else if (a) {
        graphicsEditor.close();
      }
    };
    // Back closes the editor: an entry (the same address and router state) is pushed while it is open, and Back
    // asks first when there are unsaved changes.
    const mark = () => window.history.pushState({ ...(window.history.state ?? {}), prismGraphicsEditor: true }, '');
    if (!window.history.state?.prismGraphicsEditor) mark();
    const onPop = () => {
      if (dirtyRef.current && !window.confirm(LEAVE_MESSAGE)) {
        mark();
        return;
      }
      graphicsEditor.close();
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  async function save() {
    const host = hostRef.current;
    if (!host) return;
    setBusy('save');
    try {
      await host.requestSave();
    } catch (err) {
      // Closed meanwhile (destroy() rejects pending requests with NOT_READY): nothing to say.
      if (hostRef.current !== host) return;
      const problem = saveProblem(err, errorMessage);
      if (problem) toast(problem, 'error');
    } finally {
      setBusy(null);
    }
  }

  async function close() {
    const host = hostRef.current;
    if (!host || host.state !== 'ready') {
      if (!dirtyRef.current || window.confirm(LEAVE_MESSAGE)) leaveEditor();
      return;
    }
    setBusy('close');
    // The editor asks about unsaved changes itself; if it never answers (TIMEOUT) or reloads before it does
    // (NOT_READY, bridge 1.0.1), ask here. An editor:error answer was already shown by the error handler, and the
    // editor stays open.
    let answer: Awaited<ReturnType<Host['requestClose']>>;
    try {
      answer = await host.requestClose({ timeoutMs: CLOSE_TIMEOUT_MS });
    } catch (err) {
      setBusy(null);
      if (hostRef.current !== host) return;
      const question = closeQuestion(err);
      if (question && window.confirm(question)) leaveEditor();
      return;
    }
    setBusy(null);
    if (answer.closed) leaveEditor();
  }

  const label = request.mode === 'edit' ? `Editing ${request.title ? `“${request.title}”` : 'a graphic'}`
    : request.duplicate ? `New graphic from ${request.title ? `“${request.title}”` : 'a copy'}` : 'New graphic';
  return (
    <div className="graphics-editor-layer" role="dialog" aria-modal="true" aria-label="PRISM Graphics" data-testid="graphics-editor"
      onKeyDown={(e) => e.stopPropagation()}>
      <header className="graphics-editor-bar">
        <strong>PRISM Graphics</strong>
        <span className="muted">{label}</span>
        <span className={`pill ${status === 'ready' ? (dirty ? 'warning' : 'accent') : ''}`} data-testid="graphics-editor-status">
          {status === 'connecting' ? <><Loader2 size={11} className="spin" /> Connecting…</> : status === 'error' ? 'Not connected' : dirty ? 'Unsaved changes' : 'Ready'}
        </span>
        <span style={{ flex: 1 }} />
        <button className="btn sm" disabled={status !== 'ready' || busy !== null} onClick={() => void save()} data-testid="graphics-editor-save"><Save size={14} /> Save</button>
        <button className="btn sm ghost" disabled={busy === 'close'} onClick={() => void close()} data-testid="graphics-editor-close" aria-label="Close PRISM Graphics" title="Close"><X size={15} /> Close</button>
      </header>
      {problem && <div className="graphics-editor-problem" role="alert">{problem}</div>}
      {!settings.data ? <div className="empty"><Loader2 className="spin" color="var(--accent)" /></div>
        : !origin ? <div className="empty"><div className="empty-inner"><h2>PRISM Graphics is not configured</h2><p>Ask an administrator to set PRISM_GRAPHICS_EDITOR_ORIGIN.</p></div></div>
        : (
          <iframe ref={frame} className="graphics-editor-frame" src={`${origin}/`} title="Graphics editor"
            sandbox="allow-scripts allow-same-origin allow-downloads" allow="clipboard-read; clipboard-write" referrerPolicy="no-referrer" />
        )}
    </div>
  );
}
